// @story HIR-HR30
// The rounds that carry no marks (server: services/roundRecord.service.js),
// on a stage marked as one of them in the pipeline:
//   A Day at DPS     attended / not attended, a remark, and a remark on the
//                    student demo that day (a remark, never marks)
//   Society round    attended / not attended
//   exit interview   held / not held, and remarks
// No marks and no rejection. Not attending A Day at DPS puts the candidate on
// hold. A correction is a newer record; the server keeps every earlier one.
// Nothing here messages the candidate.
import React, { useState } from 'react';
import api from '../../services/api';
import { Button } from '../../ds';

export const NO_MARK_KEYS = ['day_at_dps', 'society', 'exit_interview'];
const LABEL = { day_at_dps: 'A Day at DPS', society: 'Society round', exit_interview: 'Exit interview' };
const small = { fontSize: 'var(--fs-caption)', color: 'var(--text-muted)' };
const linkButton = { background: 'none', border: 'none', padding: 0, cursor: 'pointer', font: 'inherit', fontSize: 'var(--fs-caption)', color: 'var(--brand-primary-ink)' };
const box = { width: '100%', border: '1px solid var(--border, #ddd)', borderRadius: 6, padding: '4px 6px', font: 'inherit', fontSize: 'var(--fs-caption)' };

/* The record in force: the one with the highest seq. */
export function latest(records) {
  const list = Array.isArray(records) ? records : [];
  return list.length ? list.reduce((a, r) => ((r.seq || 0) > (a.seq || 0) ? r : a)) : null;
}

export function describe(kind, r) {
  if (!r) return 'Not recorded yet';
  if (kind === 'exit_interview') return r.status === 'held' ? 'Held' : 'Not held';
  return r.attended ? 'Attended' : 'Did not attend';
}

export default function RoundRecord({ kind, resumeId, stageId, stageResult, onChanged }) {
  const [open, setOpen] = useState(false);
  const [remark, setRemark] = useState('');
  const [demo, setDemo] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  if (!NO_MARK_KEYS.includes(kind)) return null;
  const current = latest(stageResult?.roundRecords);

  const send = async (body) => {
    setBusy(true); setErr('');
    try {
      await api.post(`/candidate-scoring/candidates/${resumeId}/stages/${stageId}/round-records`, {
        ...body, remark: remark.trim(), ...(kind === 'day_at_dps' ? { studentDemoRemark: demo.trim() } : {}),
      });
      setOpen(false); setRemark(''); setDemo('');
      if (onChanged) onChanged();
    } catch (e) {
      setErr(e.response?.data?.message || 'Could not record that just now.');
    } finally { setBusy(false); }
  };

  return (
    <div style={{ display: 'grid', gap: 4, justifyItems: 'start', whiteSpace: 'normal' }}>
      <span style={small}>
        {LABEL[kind]}: {describe(kind, current)}{current?.by?.name ? ` · ${current.by.name}` : ''} · no marks
        {kind === 'day_at_dps' && current && current.attended === false ? ' · on hold' : ''}
      </span>
      {current?.remark && <span style={small}>“{current.remark}”</span>}
      {!open ? (
        <button type="button" style={linkButton} onClick={() => setOpen(true)}>
          {current ? 'Correct the record' : `Record ${LABEL[kind]}`}
        </button>
      ) : (
        <div style={{ display: 'grid', gap: 4, minWidth: 220 }}>
          <textarea aria-label="Remark" rows={2} value={remark} onChange={(e) => setRemark(e.target.value)} placeholder="Remark (optional)" style={box} />
          {kind === 'day_at_dps' && (
            <textarea aria-label="Student demo remark" rows={2} value={demo} onChange={(e) => setDemo(e.target.value)} placeholder="Student demo that day: a remark, never marks" style={box} />
          )}
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {kind === 'exit_interview' ? (
              <>
                <Button size="sm" disabled={busy} onClick={() => send({ status: 'held' })}>Held</Button>
                <Button size="sm" variant="secondary" disabled={busy} onClick={() => send({ status: 'not_held' })}>Not held</Button>
              </>
            ) : (
              <>
                <Button size="sm" disabled={busy} onClick={() => send({ attended: true })}>Attended</Button>
                <Button size="sm" variant="secondary" disabled={busy} onClick={() => send({ attended: false })}>Did not attend</Button>
              </>
            )}
            <Button size="sm" variant="ghost" disabled={busy} onClick={() => { setOpen(false); setErr(''); }}>Cancel</Button>
          </div>
          {err && <span role="alert" style={{ ...small, color: 'var(--status-danger-text)' }}>{err}</span>}
        </div>
      )}
    </div>
  );
}
